import { canAccess } from "../../../core/router/auth/permissions";
import type { Entity } from "../../../core/types/fe";
import { FormFieldRender } from "../components";
import { isFieldVisible } from "../components/ConditionalWrapper/ConditionalWrapper";
import { createHorizontalFormField } from "../formFieldLabelPositioning/Horizontal/CreateHorizontalFormField";
import type { FormLayoutProps } from "../types";
import type { FormField, FormTab } from "../types/types";
import { FormComponent } from "../types/types";

export const DefaultFormLayout = <T extends Entity>(props: FormLayoutProps<T>) => {
    const { formDefinition, formData, onChange } = props;

    return formDefinition.tabs.map((tab: FormTab) => {
        return tab.fields.map((field: FormField) => {
            // Skip ReadOnlyCheckbox fields
            if (field.component === FormComponent.READ_ONLY_CHECK) {
                return null;
            }

            if (field.attributes?.visible === false) {
                return null;
            }

            // If the user has not writing access on this tab the fields inside this tab are disabled (read-only).
            const formIsReadOnly = !canAccess(formDefinition.resource, "canWrite");
            // OR, if the field can be disabled by a higher rule, set in field's disableIf attribute.
            const disabledByOtherField = field.disableIf ? !!formData[field.disableIf as keyof T] : false;

            // OR, if the field is disabled from a RO property in another profile.
            // e.g. RO property set in Terminal Profiles, the field in Terminals should be disabled
            const disabledByROInProfile = !!formData[`${field.name}CheckReadOnly` as keyof T];

            const readOnly = disabledByROInProfile || formIsReadOnly || (field.attributes?.readOnly ?? false) || disabledByOtherField;

            // finds the corresponding ReadOnlyCheck field when a readOnlyName is set in attributes
            const readOnlyFieldConfig = tab.fields.find((fieldConfig) => fieldConfig.name === field.attributes?.readOnlyName);

            const hasVisibility = (currentField: FormField) => isFieldVisible(formData, currentField?.attributes);
            const isVisible = hasVisibility(field) && !field.component.startsWith("Hidden");

            return (
                <FormFieldRender
                    key={`${field.name}FormField`}
                    field={field}
                    factory={createHorizontalFormField}
                    readOnly={readOnly}
                    isVisible={isVisible}
                    onChange={onChange}
                    readOnlyField={readOnlyFieldConfig}
                />
            );
        });
    });
};
